// Stands in front of every screen until this device's jobs are known to come
// from the CRM this site uses (lib/device-crm.ts, docs/SITES.md). Jobs from
// another CRM are cleared only when the person says so, and they're told what
// that loses; sync waits meanwhile, so nothing is sent to the wrong CRM.
import { useState, type ReactNode } from 'react';
import { useAtomValue } from 'jotai';
import { loadable } from 'jotai/utils';
import { Button } from '@higgins/ui';
import { PageLoading } from './page-loading';
import { checkDeviceCrm, clearForSiteCrm } from '../lib/device-crm';
import { useDeviceQuery } from '../lib/hooks';
import { meAtom } from '../store/atoms';

/** Loadable, so a failed "me" doesn't stop the screens: the CRM stays unknown and sync simply waits. */
const meLoadable = loadable(meAtom);

export function CrmSwitchGate({ children }: { children: ReactNode }) {
  const me = useAtomValue(meLoadable);
  // Tagged with the "me" state it ran for, so a check left over from before "me" answered is never trusted.
  const answer = useDeviceQuery(async () => ({ forMe: me.state, check: await checkDeviceCrm() }), [me.state]);
  const [clearing, setClearing] = useState(false);

  // No screen opens (and so no job is fetched and saved here) until the device's CRM is settled.
  if (me.state === 'loading' || answer?.forMe !== me.state) return <PageLoading />;
  const { check } = answer;
  if (check.state !== 'other') return <>{children}</>;

  async function clear() {
    setClearing(true);
    try {
      await clearForSiteCrm();
    } finally {
      setClearing(false);
    }
  }

  return (
    <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
      <p className="font-medium">The jobs on this device are from a different CRM to the one this site uses.</p>
      <p>
        Measure2Quote now has a test site (the <strong>Measure2Quote (Test)</strong> tile) for test jobs, and this site works on real jobs.
        The jobs on this device are cleared before you carry on. The jobs themselves are safe in their CRM — nothing there changes.
      </p>
      {check.waiting > 0 && (
        <p className="font-medium text-red-800">
          {check.waiting} change{check.waiting === 1 ? '' : 's'} on this device {check.waiting === 1 ? 'was' : 'were'} never sent, and will be lost.
        </p>
      )}
      <Button onClick={() => void clear()} disabled={clearing}>
        {clearing ? 'Clearing…' : 'Clear this device and carry on'}
      </Button>
    </div>
  );
}
